//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ApiClient } from './apiClient';
import { type AdminAiModelsResponse } from './apiTypes';

// The fields the pane renders decisions from: the switch binds to `enabled`,
// the Reset control and the "Server default" line to `overridden` and
// `default_enabled`, the ADEL Profile column's Select to
// `permission_profile`, and the controls' accessible names to
// `display_name`. A row missing one would render a confident falsehood (a
// default of "off", a control named "undefined") rather than fail, so the
// shape is enforced here, at the transport: a bad GET lands in the pane's
// query error, and a bad write response rejects the write, which the pane
// reports as a failed toggle. Either way the pane renders from a list it can
// trust.
const BOOLEAN_FIELDS = ['enabled', 'default_enabled', 'overridden'] as const;
const STRING_FIELDS = [
  'key', 'display_name', 'provider', 'permission_profile'
] as const;

const assertAdminAiModels = (
  res: AdminAiModelsResponse | undefined,
  what: string): AdminAiModelsResponse => {
  if (!Array.isArray(res?.models))
    throw new Error(
      `Malformed ${what} response: expected a "models" array`);
  for (const model of res.models) {
    for (const field of STRING_FIELDS)
      if (typeof model?.[field] !== 'string')
        throw new Error(
          `Malformed ${what} response: expected a string "${field}" on ` +
          `${model?.key}: ${JSON.stringify(model?.[field])}`);
    for (const field of BOOLEAN_FIELDS)
      if (typeof model[field] !== 'boolean')
        throw new Error(
          `Malformed ${what} response: expected a boolean "${field}" on ` +
          `${model.key}: ${JSON.stringify(model[field])}`);
  }
  // The pane counts these out in a sentence, so anything that is not a whole
  // count would render as "3.5 override entries" or "NaN" rather than fail.
  if (!Number.isInteger(res.orphaned_overrides) || res.orphaned_overrides < 0)
    throw new Error(
      `Malformed ${what} response: expected a non-negative integer ` +
      `"orphaned_overrides": ${JSON.stringify(res.orphaned_overrides)}`);
  return res;
};

/**
 * Admin AI-model gating: the control-panel view of every configured model,
 * the per-model enable/disable override, and the global reset that clears
 * every override at once. Admin-only; the backend returns 403 for non-admins
 * and 404 for an unknown key. Every call answers with the full post-write
 * view for the caller to seed its cache from.
 */
export class AiModelAdminService {
  constructor(private readonly api: ApiClient) {}

  getAdminAiModels = async (): Promise<AdminAiModelsResponse> => {
    const res = await this.api.makeRequest<AdminAiModelsResponse>(
      'admin/aimodels', 'get');
    return assertAdminAiModels(res, 'admin AI models');
  };

  // Writes an admin override. The wire field is `enabled` and must be a JSON
  // boolean (the backend answers 400 to a string).
  setAiModelEnabled = async (
    key: string, enabled: boolean): Promise<AdminAiModelsResponse> => {
    const res = await this.api.makeRequest<AdminAiModelsResponse>(
      `admin/aimodels/${encodeURIComponent(key)}`, 'put', { enabled });
    return assertAdminAiModels(res, 'AI model update');
  };

  // Assigns the ADEL profile this model uses. The wire field is
  // `permission_profile`; the backend answers 400 for an unknown profile name
  // and 404 for an unknown model key.
  setAiModelProfile = async (
    key: string, profile: string): Promise<AdminAiModelsResponse> => {
    const res = await this.api.makeRequest<AdminAiModelsResponse>(
      `admin/aimodels/${encodeURIComponent(key)}`, 'put',
      { permission_profile: profile });
    return assertAdminAiModels(res, 'AI model profile update');
  };

  // Removes the override so the model returns to its YAML default. Deleting an
  // override that does not exist is a 200.
  clearAiModelOverride = async (
    key: string): Promise<AdminAiModelsResponse> => {
    const res = await this.api.makeRequest<AdminAiModelsResponse>(
      `admin/aimodels/${encodeURIComponent(key)}`, 'delete');
    return assertAdminAiModels(res, 'AI model override reset');
  };

  // Drops every override in one write, orphaned entries included, returning
  // every model to its YAML default. Keyless, so it cannot be confused with
  // the per-model DELETE. Resetting when nothing is overridden is a 200.
  resetAllAiModelOverrides = async (): Promise<AdminAiModelsResponse> => {
    const res = await this.api.makeRequest<AdminAiModelsResponse>(
      'admin/aimodels', 'delete');
    return assertAdminAiModels(res, 'AI model override reset');
  };
}
